import { type Dictionary } from '@ngrx/entity';

import {
  applyPropsPatch,
  type CanvasElement,
  type CanvasElementId,
  type CanvasElementOverrides,
  type PartialPropsOf,
  type SlotId,
} from '@blaze-runtime/engine-domain';
import { STYLE_CONFIG_PROPERTIES_PATH } from '@blaze-shared/elements-domain';
import { fromStyles } from '@blaze-shared/style-state-feature';

import {
  hasProps,
  hasSlotStyleConfig,
  hasStylesConfig,
} from '../../canvas-elements/element-model/element-props-guards';

import { hasStyleableSlots } from './style-slot-target';

export interface DetachStyleSheetData {
  entities: Dictionary<CanvasElement>;
  elementId: CanvasElementId;
  slotId?: SlotId;
  styleProps: PartialPropsOf;
  styleOverrides?: CanvasElementOverrides;
}

export function detachStyleSheetFromElementMutably(data: DetachStyleSheetData): void {
  const { entities, elementId, slotId, styleProps, styleOverrides } = data;

  const element = entities[elementId];
  const slot = slotId && hasStyleableSlots(element) ? element.slots[slotId] : undefined;

  if (slot) {
    if (!hasSlotStyleConfig(slot)) {
      return;
    }

    delete slot.props[STYLE_CONFIG_PROPERTIES_PATH];
    const merged = fromStyles.mergeElementAndStyleProps(
      slot.props,
      slot.overrides,
      styleProps,
      styleOverrides,
    );

    slot.props = merged.props;
    slot.overrides = merged.overrides;

    return;
  }

  if (hasStylesConfig(element)) {
    delete element.props[STYLE_CONFIG_PROPERTIES_PATH];
    const merged = fromStyles.mergeElementAndStyleProps(
      element.props,
      element.overrides,
      styleProps,
      styleOverrides,
    );

    element.props = merged.props;
    element.overrides = merged.overrides;
  }
}

export function copyPropsFromInactiveStyleSheetMutably(
  element: CanvasElement | undefined,
  props: Record<string, any> | undefined,
): void {
  if (!hasProps(element)) {
    return;
  }

  element.props = applyPropsPatch(element.props, props ?? {}) as typeof element.props;

  if (hasStylesConfig(element)) {
    delete element.props.styleConfig;
  }
}
